"use client";
import { css } from "@emotion/react";
import GlobalStyles from "@mui/material/GlobalStyles";
import kebabCase from "lodash/kebabCase";
import b2bThemeConfig from "@/styles/themes/core/B2B/theme";
import {
  getCSSVariablesByDevice,
  getCSSVariablesWithoutDevice,
} from "@/utils/theme/getCSSVariablesByDevice";
import { stringifyObjectToCSS } from "@/utils/theme/stringifyObjectToCSS";
import { transformThemeConfigToCSSVariables } from "@/utils/theme/transformThemeConfigToCSSVariables";
import type { Theme } from "@/types/theme";

const coreThemes: Record<Theme["setup"]["coreTheme"], Theme> = {
  B2B: b2bThemeConfig,
};

export interface CssVariablesProps {
  themeConfig: Theme;
}

function getFontImport(url?: string, included?: boolean) {
  return url && included ? `@import url(${url});` : "";
}

export default function CssVariables({ themeConfig }: CssVariablesProps) {
  const informaVariables = transformThemeConfigToCSSVariables(themeConfig, [
    "--informa",
  ]);
  const informaVariablesPerDevice = {
    noDevice: getCSSVariablesWithoutDevice(informaVariables),
    mobile: getCSSVariablesByDevice(informaVariables, "mobile"),
    tablet: getCSSVariablesByDevice(informaVariables, "tablet"),
    desktop: getCSSVariablesByDevice(informaVariables, "desktop"),
  };
  const coreVariables = transformThemeConfigToCSSVariables(
    coreThemes[themeConfig.setup.coreTheme],
    ["--core"],
  );
  const coreVariablesPerDevice = {
    noDevice: getCSSVariablesWithoutDevice(coreVariables),
    mobile: getCSSVariablesByDevice(coreVariables, "mobile"),
    tablet: getCSSVariablesByDevice(coreVariables, "tablet"),
    desktop: getCSSVariablesByDevice(coreVariables, "desktop"),
  };
  const customFonts = themeConfig.setup.font.customFonts.map((fontName) =>
    getFontImport(`/static/fonts/${kebabCase(fontName)}.css`, true),
  );

  const vars = css`
    ${getFontImport(themeConfig.setup.font.googleFontUrl, true)}
    ${customFonts.join("")}

    :root {
      ${stringifyObjectToCSS(coreVariablesPerDevice.noDevice)}
      ${stringifyObjectToCSS(coreVariablesPerDevice.mobile)}
    }

    :root {
      ${stringifyObjectToCSS(informaVariablesPerDevice.noDevice)}
      ${stringifyObjectToCSS(informaVariablesPerDevice.mobile)}
    }

    @media (min-width: 768px) {
      :root {
        ${stringifyObjectToCSS(coreVariablesPerDevice.tablet)}
      }

      :root {
        ${stringifyObjectToCSS(informaVariablesPerDevice.tablet)}
      }
    }

    @media (min-width: 992px) {
      :root {
        ${stringifyObjectToCSS(coreVariablesPerDevice.desktop)}
      }

      :root {
        ${stringifyObjectToCSS(informaVariablesPerDevice.desktop)}
      }
    }

    ${themeConfig.customCSS}
  `;

  return <GlobalStyles styles={vars} />;
}
